import { Box, Dialog, Snackbar, Alert, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExoForm from "../../components/exo/ExoForm";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import ExoStepper from "../../components/exo/ExoStepper";
import StepCustomStartUp from "../../components/exo/steps/StepCustomStartUp";
import { LoadingBlur } from "../../components/LoadingBlur";
import { DBRequest } from "../../api/dbRequest";
import * as Yup from "yup";

const GlobalSettings = ({
  open,
  onClose,
  onUpdate,
  startData,
  scenario = {},
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null); // { type, text }

  const sortByDiameter = (array) =>
    [...array].sort((a, b) => parseFloat(a.diameter) - parseFloat(b.diameter));

  function prepareData(data) {
    return Array.isArray(data.available_sewer_diameters)
      ? {
          ...data,
          available_sewer_diameters: sortByDiameter(
            data.available_sewer_diameters
          ),
        }
      : data;
  }

  function handleSave(data) {
    DBRequest({
      path: `scenario_settings/${scenario.id}`,
      method: "patch",
      data: prepareData(data),
      onResponse: (resData) => {
        handleUpdateResponse(resData);
        setSaveFeedback({ type: "success", text: t("Saved") });
      },
      onLoading: setIsSaving,
      onError: (error) =>
        setSaveFeedback({
          type: "error",
          text: (error && error.message) || t("Save failed"),
        }),
    });
  }

  const scenarioDefaultsFields = [
    {
      label: t("Units"),
      key: "units",
      type: "select",
      options: [{ label: "Si (kg, m)", value: "SI" }],
    },
    {
      label: t("Auto divide large pipes"),
      key: "auto_divide_large_pipes",
      type: "checkNFields",
      fields: [
        {
          label: t("Maximum pipe length"),
          key: "max_pipe_length",
          type: "number",
          unit: "m",
        },
      ],
    },
  ];
  const scenarioValidationSchema = Yup.object().shape({
    units: Yup.string().required(t("Is required")),
    auto_divide_large_pipes: Yup.boolean(),
    max_pipe_length: Yup.number()
      .min(0, "Must be at least 0")
      .max(1000, "Must be at most 1000")
      .when("auto_divide_large_pipes", {
        is: true,
        then: (schema) => schema.required("Is required"),
        otherwise: (schema) => schema.optional(),
      }),
  });

  const wasteWaterAccumulationFields = [
    {
      label: t("Water Consumption"),
      key: "water_consumption",
      type: "number",
      unit: "L/(inh*day)",
    },
    {
      label: t("Peak Factor"),
      key: "peak_factor",
      type: "number",
    },
    {
      label: t("Wastewater Return Factor"),
      key: "wastewater_return_factor",
      type: "number",
    },
    {
      label: t("Infiltration"),
      key: "infiltration",
      type: "number",
      unit: "L/km",
    },
    {
      label: t("First Year Population"),
      key: "first_year_population",
      type: "number",
      decimalPlaces: 0,
      info: t(
        "Expected number of inhabitants in the first planning year. Determines the wastewater volume (dry weather flow) expected at the start."
      ),
    },
    {
      label: t("Final Year Population"),
      key: "final_year_population",
      type: "number",
      decimalPlaces: 0,
      info: t(
        "Expected number of inhabitants in the final planning year. Determines the future load the network is dimensioned for."
      ),
    },
  ];
  const wasteWaterAccumulationValidationSchema = Yup.object().shape({
    water_consumption: Yup.number()
      .min(0, "Must be at least 0")
      .max(1000, "Must be at most 1000")
      .required(t("Is required")),
    peak_factor: Yup.number()
      .min(1, "Must be at least 1")
      .max(10, "Must be at most 10")
      .required(t("Is required")),
    wastewater_return_factor: Yup.number()
      .min(0, "Must be at least 0")
      .max(1, "Must be at most 1")
      .required(t("Is required")),
    infiltration: Yup.number()
      .min(0, "Must be at least 0")
      .max(10, "Must be at most 10")
      .required(t("Is required")),
    first_year_population: Yup.number()
      .min(0, "Must be at least 0")
      .required("First year population is required"),

    final_year_population: Yup.number()
      .min(0, "Must be at least 0")
      .required("Final year population is required"),
  });

  const designCriteriaFields = [
    {
      label: t("Manning Roughness"),
      key: "manning_roughness",
      type: "number",
      decimalPlaces: 4,
      info: t(
        "A larger Manning roughness means a lower flow velocity, i.e. a steeper slope is required."
      ),
    },
    {
      label: t("Minimum cover"),
      key: "minimum_cover",
      type: "number",
      unit: "m",
      info: t(
        "Minimum soil cover above the pipe. Larger values mean deeper, more expensive trenches, but protect against frost and traffic loads."
      ),
    },
    {
      label: t("Maximum Velocity"),
      key: "maximum_velocity",
      type: "number",
      unit: "m/s",
    },
    {
      label: t("Maximum Slope"),
      key: "maximum_slope",
      type: "number",
      unit: "%",
    },
    {
      label: t("Maximal buried depth"),
      key: "maximal_buried_depth",
      type: "number",
      unit: "m",
    },
    {
      label: t("Use lift stations where excavation depth > max. depth"),
      key: "use_lift_stations_where_excavation_depth",
      type: "checkbox",
      warningWhenUnchecked: t(
        "If this box is not checked, the maximum depth will be ignored and may be exceeded"
      ),
    },
    {
      label: t("Use minimum shear stress"),
      key: "use_minimum_shear_stress",
      type: "checkNFields",
      fields: [
        {
          label: t("Minimum shear stress"),
          key: "minimum_shear_stress",
          type: "number",
          info: t(
            "Minimum bed shear stress required for self-cleaning (to avoid sediment deposits). Higher values force steeper slopes."
          ),
        },
      ],
    },
  ];
  const designCriteriaValidationSchema = Yup.object().shape({
    use_lift_stations_where_excavation_depth: Yup.boolean().required(
      t("Is required")
    ),
    use_minimum_shear_stress: Yup.boolean(),
    minimum_shear_stress: Yup.number()
      .min(0.0, "Must be at least 0.0")
      .max(5.0, "Must be at most 5.0")
      .when("use_minimum_shear_stress", {
        is: true,
        then: (schema) => schema.required("Is required"),
        otherwise: (schema) => schema.nullable().optional(),
      }),
    manning_roughness: Yup.number()
      .min(0, "Must be at least 0")
      .max(1, "Must be at most 1")
      .required(t("Is required")),
    minimum_cover: Yup.number()
      .min(0, "Must be at least 0")
      .max(2, "Must be at most 2")
      .required(t("Is required")),
    maximal_buried_depth: Yup.number()
      .min(2, "Must be at least 2")
      .max(100, "Must be at most 100")
      .required(t("Is required")),
    maximum_velocity: Yup.number()
      .min(0.1, "Must be at least 0.1")
      .max(15, "Must be at most 15")
      .required(t("Is required")),
    maximum_slope: Yup.number()
      .min(0, "Must be at least 0")
      .max(50, "Must be at most 50")
      .required(t("Is required")),
  });

  const diameterFields = [
    {
      label: t("Available sewer diameters"),
      key: "available_sewer_diameters",
      type: "diameterTable",
    },
  ];
  const diameterValidationSchema = Yup.object().shape({
    available_sewer_diameters: Yup.array().of(
      Yup.object().shape({
        minimum_velocity: Yup.number()
          .min(0.1, "Must be at least 0.1")
          .max(5, "Must be at most 5")
          .required(t("Is required")),
        minimum_slope: Yup.number()
          .min(0, "Must be at least 0")
          .max(5, "Must be at most 5")
          .required(t("Is required")),
        diameter: Yup.number().required(t("Is required")),
        maximal_water_depth: Yup.number()
          .min(0, "Must be at least 0")
          .max(1, "Must be at most 1")
          .required(t("Is required")),
      })
    ),
  });

  const steps = [
    {
      label: t("Scenario Defaults"),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepCustomStartUp
          fields={scenarioDefaultsFields}
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          validationSchema={scenarioValidationSchema}
        />
      ),
    },
    {
      label: t("Waste Water Accumulation"),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepCustomStartUp
          fields={wasteWaterAccumulationFields}
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          validationSchema={wasteWaterAccumulationValidationSchema}
        />
      ),
    },
    {
      label: t("Design Criteria"),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepCustomStartUp
          fields={designCriteriaFields}
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          validationSchema={designCriteriaValidationSchema}
        />
      ),
    },
    {
      label: t("Diameter"),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepCustomStartUp
          fields={diameterFields}
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          validationSchema={diameterValidationSchema}
        />
      ),
    },
  ];

  const finalStep = {
    operation: ({
      dataCollection,
      handleResponse,
      setIsLoading,
      handleError,
    }) => {
      setIsLoading(true);

      const sortByDiameter = (array) => {
        return array.sort(
          (a, b) => parseFloat(a.diameter) - parseFloat(b.diameter)
        );
      };

      //order diameter
      const sortedDataCollection = {
        ...dataCollection,
        available_sewer_diameters: sortByDiameter(
          dataCollection.available_sewer_diameters
        ),
      };

      DBRequest({
        path: `scenario_settings/${scenario.id}`,
        method: "patch",
        data: sortedDataCollection,
        onResponse: handleUpdateResponse,
        onLoading: setIsLoading,
        onError: handleError,
      });
    },
    content: (response) => <Box>Saved</Box>,
  };

  function handleUpdateResponse(data) {
    onUpdate({ ...scenario, globalSettings: data.globalSettings });
  }

  const defaultData = {
    units: "SI",
    auto_divide_large_pipes: false,
    max_pipe_length: 100,
    water_consumption: 120,
    peak_factor: 1.8,
    wastewater_return_factor: 0.8,
    infiltration: 0.1,
    first_year_population: 0,
    final_year_population: 0,
    minimum_velocity: 0.6,
    maximum_velocity: 5,
    minimum_slope: 0.33,
    maximum_slope: 10,
    use_minimum_shear_stress: false,
    minimum_shear_stress: 1,
    manning_roughness: 0.013,
    maximal_water_depth: 0.8,
    minimum_cover: 1,
    maximal_buried_depth: 5,
    available_sewer_diameters: null, // make sure the default values are loaded correctly
    use_lift_stations_where_excavation_depth: true,
    max_number_of_pump_stations: null,
    max_outfall: 1,
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          maxHeight: "90vh",
          height: "800px",
          margin: 0,
          width: "1200px",
          maxWidth: "100vw!important",
          overflowX: "auto",
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label={t("Close")}
        sx={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}
      >
        <CloseIcon />
      </IconButton>
      <ExoStepper
        label={t("Global Settings")}
        onClose={onClose}
        data={steps}
        finalStep={finalStep}
        startDataCollection={startData || defaultData}
        onSave={handleSave}
      />
      <LoadingBlur active={isSaving} text={t("Saving...")} />
      <Snackbar
        open={Boolean(saveFeedback)}
        autoHideDuration={3000}
        onClose={() => setSaveFeedback(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {saveFeedback ? (
          <Alert
            onClose={() => setSaveFeedback(null)}
            severity={saveFeedback.type}
            variant="filled"
          >
            {saveFeedback.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Dialog>
  );
};

export default GlobalSettings;
